// REQ-P1-01c: 契約(contracts/openapi.yaml)から生成された型に基づくAPIクライアント。
//
// - パス/メソッド/リクエスト/レスポンスは types.gen.ts(自動生成・編集禁止)で型付けされる。
//   契約と実装が乖離すると、ここを使う呼び出し側が typecheck で落ちる。
// - 認証Cookie/CSRFヘッダは setupFetchWithCredentials.js がグローバルfetchに
//   適用済みのため、本クライアントはそれを素通しで継承する。
// - 新規のAPI呼び出しは生fetchではなく本クライアント経由とする(architecture-baselines
//   ガードのfetch凍結対象外にするための唯一の正規ルート)。

import createClient from 'openapi-fetch';
import type { paths } from './types.gen';
import { decodeJsonResponse } from './jsonResponse';
import { JSON_RESPONSE_OPERATIONS } from './jsonOperations.gen';

const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const DEFAULT_API_GET_TIMEOUT_MS = 15_000;
const MAX_API_GET_TIMEOUT_MS = 60_000;
const pendingSafeApiReads = new Map<string, Promise<Response>>();

function currentApiOrigin(): string {
  return (process.env.REACT_APP_API_URL || API_ORIGIN || '').replace(/\/+$/, '');
}

function resolveApiGetTimeoutMs(): number {
  const raw = Number(process.env.REACT_APP_API_GET_TIMEOUT_MS);
  if (!Number.isFinite(raw)) return DEFAULT_API_GET_TIMEOUT_MS;
  if (raw <= 0) return 0;
  return Math.min(Math.max(Math.floor(raw), 1_000), MAX_API_GET_TIMEOUT_MS);
}

function resolveFetchMethod(input: RequestInfo | URL, init?: RequestInit): string {
  const fromInit = init?.method;
  if (fromInit) return String(fromInit).toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return String(input.method || 'GET').toUpperCase();
  }
  return 'GET';
}

function resolveFetchUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  return '';
}

function isConfiguredApiUrl(url: string): boolean {
  if (!url) return false;
  try {
    const apiOrigin = currentApiOrigin();
    const base = apiOrigin || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const resolved = new URL(url, base);
    if (!resolved.pathname.startsWith('/api/')) return false;
    if (!apiOrigin) return true;
    return resolved.origin === new URL(apiOrigin).origin;
  } catch {
    return false;
  }
}

function normalizeApiReadUrlForKey(url: string): string {
  try {
    const apiOrigin = currentApiOrigin();
    const base = apiOrigin || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const resolved = new URL(url, base);
    ['_', '_t', 'cacheBust', 'cacheBuster', 'timestamp'].forEach((key) => {
      resolved.searchParams.delete(key);
    });
    resolved.hash = '';
    return resolved.toString();
  } catch {
    return String(url || '');
  }
}

function normalizeHeadersForKey(headers?: HeadersInit | null): [string, string][] {
  if (!headers) return [];
  try {
    return Array.from(new Headers(headers).entries())
      .filter(([key]) => !['x-request-id', 'user-agent'].includes(key.toLowerCase()))
      .sort(([left], [right]) => left.localeCompare(right));
  } catch {
    return [];
  }
}

function resolveFetchHeaders(input: RequestInfo | URL, init?: RequestInit): [string, string][] {
  const headers = normalizeHeadersForKey(init?.headers);
  if (headers.length > 0) return headers;
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return normalizeHeadersForKey(input.headers);
  }
  return [];
}

function shouldCoalesceApiRead(input: RequestInfo | URL, init?: RequestInit): boolean {
  if (init?.signal) return false;
  const method = resolveFetchMethod(input, init);
  if (method !== 'GET' && method !== 'HEAD') return false;
  return isConfiguredApiUrl(resolveFetchUrl(input));
}

function buildApiReadCoalesceKey(input: RequestInfo | URL, init?: RequestInit): string | null {
  if (!shouldCoalesceApiRead(input, init)) return null;
  return JSON.stringify({
    method: resolveFetchMethod(input, init),
    url: normalizeApiReadUrlForKey(resolveFetchUrl(input)),
    headers: resolveFetchHeaders(input, init),
  });
}

function shouldApplyApiGetTimeout(input: RequestInfo | URL, init?: RequestInit): boolean {
  if (init?.signal) return false;
  const method = resolveFetchMethod(input, init);
  if (method !== 'GET' && method !== 'HEAD') return false;
  return resolveApiGetTimeoutMs() > 0 && isConfiguredApiUrl(resolveFetchUrl(input));
}

async function runFetchViaConfiguredGlobal(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (
    process.env.NODE_ENV === 'test' &&
    typeof Request !== 'undefined' &&
    input instanceof Request
  ) {
    const request = input as Request;
    const apiOrigin = currentApiOrigin();
    const requestUrl = request.url.startsWith('/')
      ? `${apiOrigin}${request.url}`
      : request.url;
    const requestInit: RequestInit = {
      ...init,
      method: request.method,
      headers: request.headers,
      credentials: request.credentials === 'omit' ? 'omit' : 'include',
    };
    if (!['GET', 'HEAD'].includes(request.method.toUpperCase())) {
      const requestWithBody = request as Request & { _bodyInit?: BodyInit };
      requestInit.body =
        requestWithBody._bodyInit !== undefined
          ? requestWithBody._bodyInit
          : await request.clone().text();
    }
    const response = await fetch(requestUrl, requestInit);
    invalidateReadsAfterWrite(response, requestUrl, request.method);
    return response;
  }
  const response = await fetch(input, init);
  invalidateReadsAfterWrite(response, resolveFetchUrl(input), resolveFetchMethod(input, init));
  return response;
}

function invalidateReadsAfterWrite(response: Response, url: string, method: string): void {
  if (response.ok && isConfiguredApiUrl(url) && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    pendingSafeApiReads.clear();
  }
}

async function fetchViaConfiguredGlobalOnce(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (!shouldApplyApiGetTimeout(input, init)) {
    return runFetchViaConfiguredGlobal(input, init);
  }
  const timeoutMs = resolveApiGetTimeoutMs();
  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await runFetchViaConfiguredGlobal(input, {
      ...init,
      signal: controller.signal,
    });
  } catch (error) {
    if (controller.signal.aborted) {
      throw Object.assign(new Error(`API GET timed out after ${timeoutMs}ms`), {
        name: 'AbortError',
        isNetworkError: true,
      });
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

export async function fetchViaConfiguredGlobal(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const coalesceKey = buildApiReadCoalesceKey(input, init);
  if (!coalesceKey) {
    return fetchViaConfiguredGlobalOnce(input, init);
  }

  const pending = pendingSafeApiReads.get(coalesceKey);
  if (pending) {
    return (await pending).clone();
  }

  const request = fetchViaConfiguredGlobalOnce(input, init);
  pendingSafeApiReads.set(coalesceKey, request);
  try {
    return (await request).clone();
  } finally {
    if (pendingSafeApiReads.get(coalesceKey) === request) {
      pendingSafeApiReads.delete(coalesceKey);
    }
  }
}

export const api = createClient<paths>({
  baseUrl: `${API_ORIGIN}/api`,
  credentials: 'include',
  fetch: fetchViaConfiguredGlobal,
});

api.use({
  async onResponse({ response, request, options, schemaPath }) {
    if (response.ok && (options.parseAs === 'json' || JSON_RESPONSE_OPERATIONS.has(`${request.method} ${schemaPath}`))) {
      decodeJsonResponse(response, await response.clone().text(), request.method);
    }
  },
});

type ApiRequestJsonOptions = {
  method?: string;
  query?: Record<string, unknown> | URLSearchParams;
  body?: unknown;
  headers?: HeadersInit;
  credentials?: RequestCredentials;
  signal?: AbortSignal;
  cache?: RequestCache;
  keepalive?: boolean;
};

type ApiRequestJsonResult<T = unknown> = {
  data: T | null;
  error?: unknown;
  response: Response;
};

function appendQuery(url: string, query?: Record<string, unknown> | URLSearchParams): string {
  if (!query) return url;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const resolved = new URL(url, origin);
  if (query instanceof URLSearchParams) {
    query.forEach((value, key) => {
      if (value !== '') resolved.searchParams.set(key, value);
    });
  } else {
    Object.entries(query).forEach(([key, value]) => {
      if (value == null || value === '') return;
      resolved.searchParams.set(key, String(value));
    });
  }
  if (/^https?:\/\//i.test(url)) return resolved.toString();
  return `${resolved.pathname}${resolved.search}`;
}

function resolveApiRequestUrl(path: string, query?: Record<string, unknown> | URLSearchParams): string {
  const value = String(path || '');
  const isAbsolute = /^https?:\/\//i.test(value);
  const normalized = isAbsolute
    ? value
    : value.startsWith('/api/')
      ? `${API_ORIGIN}${value}`
      : `${API_ORIGIN}/api${value.startsWith('/') ? value : `/${value}`}`;
  return appendQuery(normalized, query);
}

export async function requestApiJson<T = unknown>(
  path: string,
  options: ApiRequestJsonOptions = {}
): Promise<ApiRequestJsonResult<T>> {
  const method = (options.method || 'GET').toUpperCase();
  const headers = new Headers(options.headers || {});
  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    if (
      typeof options.body === 'string' ||
      (typeof FormData !== 'undefined' && options.body instanceof FormData) ||
      (typeof Blob !== 'undefined' && options.body instanceof Blob)
    ) {
      body = options.body;
    } else {
      if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
      body = JSON.stringify(options.body);
    }
  }
  const response = await fetchViaConfiguredGlobal(resolveApiRequestUrl(path, options.query), {
    method,
    credentials: options.credentials || 'include',
    headers,
    body,
    signal: options.signal,
    cache: options.cache,
    keepalive: options.keepalive,
  });
  const data = decodeJsonResponse(response, await response.clone().text(), method);
  return {
    data: data as T | null,
    error: response.ok ? undefined : data,
    response,
  };
}

export type { paths } from './types.gen';
